import { HttpClient, HttpClientRequest, HttpClientResponse } from '@effect/platform';
import { GLOBAL_CONFIG } from '@silo/config';
import { RevenueData } from '@silo/data';
import { Address, makeAddressSync, Usd, USD_ZERO } from '@silo/types';
import { useSiloWeb3 } from '@silo/web3';
import { QueryFunction, useQuery } from '@tanstack/react-query';
import { Effect, pipe } from 'effect';
import { isNotNullable } from 'effect/Predicate';

export const revenueDataKey = 'revenueData';

type RevenueDataKey = [key: typeof revenueDataKey, user: Address | null];

export function useQueryRevenueData() {
  const { account } = useSiloWeb3();

  const user = account ? makeAddressSync(account) : null;

  return useQuery({
    queryKey: [revenueDataKey, user],
    queryFn: queryRevenueData,
  });
}

export function useQueryProtocolRevenueBalanceUsd(): Usd {
  const { data } = useQueryRevenueData();
  const { protocolKey } = useSiloWeb3();

  if (protocolKey === GLOBAL_CONFIG.revenue.protocolKey && isNotNullable(data)) {
    return data.revenueBalanceUsd;
  }

  return USD_ZERO;
}

const queryRevenueData: QueryFunction<RevenueData, RevenueDataKey> = async ({ queryKey: [_, user] }) => {
  let endpoint = '/api/revenue';

  if (isNotNullable(user)) {
    endpoint += `?user=${user}`;
  }

  const result = await pipe(
    HttpClientRequest.get(endpoint),
    HttpClient.fetchOk,
    Effect.andThen(HttpClientResponse.schemaBodyJson(RevenueData)),
    Effect.scoped,
    Effect.runPromise
  );

  return result;
};
